import React from 'react';

interface ModernCardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  gradient?: boolean;
  glass?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  border?: boolean;
  onClick?: () => void;
}

const ModernCard: React.FC<ModernCardProps> = ({
  children,
  className = '',
  hover = true,
  gradient = false,
  glass = false,
  padding = 'md',
  rounded = 'xl',
  shadow = 'sm',
  border = true,
  onClick,
}) => {
  const baseClasses = 'card transition-all duration-300';
  
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10',
  };
  
  const roundedClasses = {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
  };
  
  const shadowClasses = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl',
  };
  
  const hoverClasses = hover ? 'hover:shadow-lg hover:-translate-y-1' : '';
  const gradientClasses = gradient ? 'bg-gradient-to-br from-white to-neutral-50' : 'bg-white';
  const glassClasses = glass ? 'glass' : '';
  const borderClasses = border ? 'border border-neutral-200' : 'border-0';
  const clickableClasses = onClick ? 'cursor-pointer' : '';
  
  const cardClasses = [
    baseClasses,
    paddingClasses[padding],
    roundedClasses[rounded],
    shadowClasses[shadow],
    hoverClasses,
    gradientClasses,
    glassClasses,
    borderClasses,
    clickableClasses,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses} onClick={onClick}>
      {children}
    </div>
  );
};

interface ModernCardHeaderProps {
  children: React.ReactNode;
  className?: string;
  gradient?: boolean;
}

export const ModernCardHeader: React.FC<ModernCardHeaderProps> = ({
  children,
  className = '',
  gradient = false,
}) => {
  const gradientClasses = gradient ? 'bg-gradient-to-r from-primary-50 to-secondary-50' : 'bg-gradient-to-r from-neutral-50 to-white';
  
  return (
    <div className={`card-header ${gradientClasses} ${className}`}>
      {children}
    </div>
  );
};

interface ModernCardBodyProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export const ModernCardBody: React.FC<ModernCardBodyProps> = ({
  children,
  className = '',
  padding = 'md',
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10',
  };
  
  return (
    <div className={`card-body ${paddingClasses[padding]} ${className}`}>
      {children}
    </div>
  );
};

interface ModernCardFooterProps {
  children: React.ReactNode;
  className?: string;
  gradient?: boolean;
}

export const ModernCardFooter: React.FC<ModernCardFooterProps> = ({
  children,
  className = '',
  gradient = false,
}) => {
  const gradientClasses = gradient ? 'bg-gradient-to-r from-neutral-50 to-primary-50' : 'bg-neutral-50';
  
  return (
    <div className={`card-footer ${gradientClasses} ${className}`}>
      {children}
    </div>
  );
};

export default ModernCard;
